import React, { useLayoutEffect, useRef } from "react";
import { View, StyleSheet } from "react-native";

import { useI18n } from "../../shared/I18n";
import { useTheme } from "../ThemeProvider";
import type { StrictPaddingStyle } from "../util/types";
import {
  DASH_MAXIMUM,
  DASH_MINIMUM,
  getSpinnerCyclePhase,
  PULSE_PERIOD_MS,
  ROTATION_PERIOD_MS,
} from "./spinnerClock";

const SIZE = 24;
// Scale the 100-unit SVG geometry to this 24-unit viewBox.
const TRACK_STROKE_WIDTH = (SIZE * 9) / 100;
const ARC_STROKE_WIDTH = (SIZE * 10) / 100;
const RADIUS = (SIZE * 45) / 100;
const CENTER = SIZE / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const ROTATE_KEYFRAMES = "mads-spinner-rotate";
const PULSE_KEYFRAMES = "mads-spinner-pulse";
const EASE_IN_OUT_QUAD = "cubic-bezier(0.455, 0.03, 0.515, 0.955)";

// CSS animations keep running while the main thread is busy (the rotation is composited),
// unlike Animated's JS driver, which is the only driver available on web.
const KEYFRAMES_CSS = `
@keyframes ${ROTATE_KEYFRAMES} {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
@keyframes ${PULSE_KEYFRAMES} {
  0%, 100% { stroke-dashoffset: ${CIRCUMFERENCE * DASH_MINIMUM}px; animation-timing-function: ${EASE_IN_OUT_QUAD}; }
  50% { stroke-dashoffset: ${CIRCUMFERENCE * DASH_MAXIMUM}px; animation-timing-function: ${EASE_IN_OUT_QUAD}; }
}
`;

// Applied after mount rather than as an `animation-delay` so server-rendered markup does not
// depend on the server's clock and hydrate with a mismatched style.
function syncToSharedClock(element: Element | null, periodMs: number) {
  for (const animation of element?.getAnimations?.() ?? []) {
    animation.currentTime = getSpinnerCyclePhase(periodMs) * periodMs;
  }
}

export interface SpinnerProps {
  /**
   * Whether to use inverse colors for the spinner.
   */
  inverse?: boolean;

  /**
   * Aria label for the spinner.
   */
  ariaLabel?: string;

  /**
   * Defines the padding style overrides.
   */
  sx?: StrictPaddingStyle;

  /**
   * Add the accessibilityLabel
   */
  accessibilityLabel?: string;

  /**
   * Hides the spinner from the screenreader
   */
  ariaHidden?: boolean;
}

export function Spinner({
  inverse = false,
  sx,
  ariaLabel,
  accessibilityLabel,
  ariaHidden = false,
}: SpinnerProps) {
  const theme = useTheme();
  const gt = useI18n();

  const rotationRef = useRef<HTMLDivElement>(null);
  const pulseRef = useRef<SVGCircleElement>(null);

  useLayoutEffect(() => {
    syncToSharedClock(rotationRef.current, ROTATION_PERIOD_MS);
    syncToSharedClock(pulseRef.current, PULSE_PERIOD_MS);
  }, []);

  const backgroundColor = inverse ? theme.colors.spinnerInverse : theme.colors.spinner;
  const highlightColor = inverse ? theme.colors.background : theme.colors.backgroundPrimary;

  const a11yLabel = ariaLabel ?? accessibilityLabel ?? gt.gettext("Loading");
  const a11yProps = ariaHidden ? { "aria-hidden": true } : { role: "img", "aria-label": a11yLabel };

  return (
    <View style={[styles.container, sx]}>
      <style href="mads-spinner-keyframes" precedence="default">
        {KEYFRAMES_CSS}
      </style>
      <div
        ref={rotationRef}
        style={{
          width: SIZE,
          height: SIZE,
          animation: `${ROTATE_KEYFRAMES} ${ROTATION_PERIOD_MS}ms linear infinite`,
        }}
      >
        <svg
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          data-testid="spinner"
          style={{ display: "block" }}
          {...a11yProps}
        >
          <circle
            data-testid="spinner-circle"
            cx={CENTER}
            cy={CENTER}
            r={RADIUS}
            stroke={backgroundColor}
            strokeWidth={TRACK_STROKE_WIDTH}
            fill="none"
          />
          <circle
            ref={pulseRef}
            data-testid="spinner-animated-circle"
            cx={CENTER}
            cy={CENTER}
            r={RADIUS}
            stroke={highlightColor}
            fill="none"
            strokeWidth={ARC_STROKE_WIDTH}
            strokeLinecap="round"
            strokeDasharray={`${CIRCUMFERENCE}, ${CIRCUMFERENCE}`}
            strokeDashoffset={CIRCUMFERENCE * DASH_MINIMUM}
            style={{
              animation: `${PULSE_KEYFRAMES} ${PULSE_PERIOD_MS}ms linear infinite`,
            }}
          />
        </svg>
      </div>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: "center",
    justifyContent: "center",
  },
});
